import * as XLSX from 'xlsx';

const loadHierarchyLookup = async () => {
  try {
    console.log('📚 Loading hierarchy lookup...');
    const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
    const response = await fetch(`${API_BASE}/data/2024%20COMPLETE%20ACH%20MCH%20HIERARCHY_Material%20Group%20Look-Up%20Table%28VALID%20HIERARCHY%29.csv`);
    
    if (!response.ok) {
      console.warn('⚠️ Hierarchy file not found, proceeding without department mapping');
      return new Map();
    }
    
    const text = await response.text();
    console.log('📄 CSV file loaded, size:', text.length, 'characters');
    
    // Parse CSV (simple approach - split by lines and commas)
    const lines = text.split('\n').filter(line => line.trim());
    console.log('📊 CSV lines found:', lines.length);
    
    const lookup = new Map();
    
    // Process each row (skip header)
    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].split(',');
      if (row.length > 8) {
        const hierarchyNode = row[0]?.trim(); // Column A
        const dpt = row[3]?.trim(); // Column D
        const department = row[8]?.trim(); // Column I
        
        // Store both exact match and 5-digit match
        if (hierarchyNode && department) {
          lookup.set(hierarchyNode, department);
        }
        if (dpt && department && dpt.length >= 5) {
          lookup.set(dpt, department);
        }
      }
    }
    
    console.log('✅ Hierarchy lookup loaded:', lookup.size, 'mappings');
    console.log('📋 Sample mappings:', Array.from(lookup.entries()).slice(0, 3));
    return lookup;
  } catch (error) {
    console.error('❌ Error loading hierarchy:', error);
    return new Map();
  }
};

const getDepartmentName = (materialGroup, hierarchyLookup) => {
  if (!materialGroup) return 'Unknown';
  
  const materialGroupStr = String(materialGroup).trim();
  
  // Try exact match first
  if (hierarchyLookup.has(materialGroupStr)) {
    const dept = hierarchyLookup.get(materialGroupStr);
    console.log(`🎯 Exact match: ${materialGroupStr} → ${dept}`);
    return dept;
  }
  
  // Try first 5 digits
  const first5 = materialGroupStr.substring(0, 5);
  if (first5.length >= 5 && hierarchyLookup.has(first5)) {
    const dept = hierarchyLookup.get(first5);
    console.log(`🎯 5-digit match: ${materialGroupStr} (${first5}) → ${dept}`);
    return dept;
  }
  
  // Fallback to material group if no match
  console.log(`⚠️ No match for: ${materialGroupStr}`);
  return `Unknown (${materialGroupStr})`;
};

export const processWarehouseInventory = async () => {
  console.log('🏭 Processing warehouse inventory...');
  try {
    // Load hierarchy lookup first
    const hierarchyLookup = await loadHierarchyLookup();
    
    const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
    console.log('📁 Fetching file:', `${API_BASE}/data/w004%20OH.xlsx`);
    const response = await fetch(`${API_BASE}/data/w004%20OH.xlsx`);
    console.log('📥 Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    console.log('📊 ArrayBuffer size:', arrayBuffer.byteLength, 'bytes');
    
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    console.log('📋 Sheets found:', workbook.SheetNames);
    
    // Use first sheet
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    console.log('📊 Total rows in file:', data.length);
    console.log('📝 Header row (first 6 columns):', data[0]?.slice(0, 6));
    console.log('📝 Sample data row (first 6 columns):', data[1]?.slice(0, 6));

    // Skip header row and filter out empty rows
    const rows = data.slice(1).filter(row => row && row[1]); // Has material in column B
    console.log('📊 Valid data rows:', rows.length);
    
    if (rows.length === 0) {
      console.warn('⚠️ No valid data rows found');
    }
    
    // Count unique SKUs (column B, index 1)
    const uniqueSkus = new Set(
      rows
        .map(row => row[1]) // Column B - Material
        .filter(sku => sku && sku !== '' && typeof sku !== 'undefined')
    );
    
    console.log('📦 Unique SKUs found:', uniqueSkus.size);
    console.log('📦 Sample SKUs:', Array.from(uniqueSkus).slice(0, 5));
    
    // First aggregate by Material Group (column F, index 5)
    const materialGroups = {};
    rows.forEach((row, index) => {
      const material = row[1]; // Column B - Material
      const qty = parseFloat(row[3]) || 0; // Column D - Unrestricted quantity
      const group = row[5] || 'Unknown'; // Column F - Material Group
      
      // Log first few rows for debugging
      if (index < 3) {
        console.log(`Row ${index}:`, {
          material,
          qty,
          group,
          rawRow: row.slice(0, 6)
        });
      }
      
      if (material && material !== '') {
        if (!materialGroups[group]) {
          materialGroups[group] = 0;
        }
        materialGroups[group] += qty;
      }
    });
    
    console.log('📦 Material groups found:', Object.keys(materialGroups).length);
    console.log('📋 Sample material groups:', Object.keys(materialGroups).slice(0, 5));
    
    // Now map Material Groups to Departments and aggregate
    const departments = {};
    Object.entries(materialGroups).forEach(([materialGroup, qty]) => {
      const department = getDepartmentName(materialGroup, hierarchyLookup);
      
      if (!departments[department]) {
        departments[department] = 0;
      }
      departments[department] += qty;
    });
    
    console.log('📊 Departments found:', Object.keys(departments).length);
    console.log('🔝 Sample departments:', Object.keys(departments).slice(0, 5));
    
    // Convert to array and sort by quantity
    const drillDownData = Object.entries(departments)
      .map(([department, qty]) => ({
        department: department,
        quantity: Math.round(qty)
      }))
      .sort((a, b) => b.quantity - a.quantity);
    
    console.log('📊 Departments processed:', drillDownData.length);
    console.log('🔝 Top 3 departments:', drillDownData.slice(0, 3));
    
    // Calculate total cost value from column G (OH $)
    const totalCostValue = rows.reduce((sum, row) => {
      const costValue = parseFloat(row[6]) || 0; // Column G - OH $
      return sum + costValue;
    }, 0);
    
    console.log('💰 Total cost value calculated:', totalCostValue);

    const result = {
      totalSkus: uniqueSkus.size,
      totalQuantity: rows.reduce((sum, row) => sum + (parseFloat(row[3]) || 0), 0),
      totalCostValue: Math.round(totalCostValue), // Round to nearest dollar
      drillDownData
    };
    
    console.log('🎯 Final inventory result:', result);
    console.log('✅ Returning totalSkus:', result.totalSkus);
    
    return result;
  } catch (error) {
    console.error('❌ Error processing warehouse inventory:', error);
    return {
      totalSkus: null,
      totalQuantity: null,
      totalCostValue: null,
      drillDownData: []
    };
  }
};